import { categoryNameToHandle } from '@lambda/reebelo/src/commonHelpers';
import {
  OfferSortByDirection,
  OfferSortBy,
  SearchParamsT,
  ProductFiltersT,
} from '@lambda/apis/src/catalog-v2/types';
import { ParsedUrlQuery } from 'querystring';
import { Logger } from '@lambda/commons/logging/logger';
import { BuildParamsT, FilterType, SelectedFilters } from './types';
import { DEFAULT_SORT_BY, DEFAULT_SORT_BY_DIRECTION } from './constants';
import { generateWithTrackingParams } from '@/components/v2/product/hooks/useStreamlineUrl';

const logger = new Logger('storefront-themes/catalog-v2');

// --- TEMPORARY: hide retired/legacy facet values from the v2 collection filters.
// REMOVE this constant, the stripHiddenFilterValues
// helper, and its two call sites in useCollectionPage.tsx once the MsCatalog2
// catalog-filter Redis cache is invalidated and the underlying data is cleaned up.
const HIDDEN_FILTER_VALUES: Record<string, string[]> = {
  Condition: ['Premium', 'Excellent', 'Acceptable'],
  'Battery Health': ['90'],
};

export const stripHiddenFilterValues = (
  filters: ProductFiltersT,
): ProductFiltersT => {
  if (!filters.variants) {
    return filters;
  }

  return {
    ...filters,
    variants: filters.variants.map((variant) => {
      const hidden = HIDDEN_FILTER_VALUES[variant.name];

      if (!hidden) {
        return variant;
      }

      return {
        ...variant,
        values: variant.values.filter((v) => !hidden.includes(v.value)),
      };
    }),
  };
};

export const generateCategoryPageUrl = (
  categoryName: string,
  categoryPk: string,
) => {
  const categoryHandle = categoryNameToHandle(categoryName);

  switch (categoryHandle) {
    // For Top 4 Categories, use Collection Nav Pages
    case 'smartphones':
      return '/collections/smartphones';
    case 'smartwatches':
      return '/collections/smartwatches';
    case 'tablets':
      return '/collections/tablets';
    case 'laptops':
      return '/collections/laptops';
    default:
      return `/collections/${categoryHandle}/${categoryPk}`;
  }
};

export const buildParams = ({
  selectedFilters,
  fixedFilters,
  sort,
  offset,
  limit,
  page,
  defaultPriceRange,
  routerQuery = {},
}: BuildParamsT): URLSearchParams => {
  logger.debug(
    { selectedFilters, fixedFilters, sort, offset, limit },
    'Building MsCatalog2 request params',
  );

  const params = generateWithTrackingParams(routerQuery);

  // --- Fixed filters (static context)
  Object.entries(fixedFilters).forEach(([key, val]) => {
    if (val == null) {
      return;
    }

    if (
      ['categoryPks', 'categories'].includes(key) &&
      selectedFilters.categories?.length
    ) {
      // Skip categoryPks and categories if categoryId are selected in filters
      return;
    }

    if (Array.isArray(val)) {
      val.forEach((v) => params.append(key, String(v)));
    } else {
      params.set(key, String(val));
    }
  });

  // --- Categories
  if (selectedFilters.categories?.length) {
    params.set(
      'categoryIds',
      selectedFilters.categories.map((c) => c.id).join(','),
    );
  }

  // --- Brands
  if (selectedFilters.brands?.length) {
    params.set('brandIds', selectedFilters.brands.map((b) => b.id).join(','));
  }

  // --- Variants
  Object.entries(selectedFilters.variants ?? {}).forEach(
    ([variantId, values]) => {
      values.forEach((v) => {
        params.append(`variants[${variantId}][]`, v);
      });
    },
  );

  // --- Price Range (if different from default)
  if (
    selectedFilters?.priceRange &&
    (selectedFilters?.priceRange?.min !== defaultPriceRange?.min ||
      selectedFilters?.priceRange?.max !== defaultPriceRange?.max)
  ) {
    params.set('minPrice', String(selectedFilters.priceRange.min));
    params.set('maxPrice', String(selectedFilters.priceRange.max));
  }

  // --- Sorting (if different from default)
  if (
    sort &&
    (sort.sortBy !== DEFAULT_SORT_BY ||
      sort.sortDirection !== DEFAULT_SORT_BY_DIRECTION)
  ) {
    params.set('sortBy', sort.sortBy);
    params.set('sortDirection', sort.sortDirection);
  }

  // --- Pagination
  if (typeof offset === 'number' && offset > 0) {
    params.set('offset', String(offset));
  }

  if (typeof limit === 'number' && limit > 0) {
    params.set('limit', String(limit));
  }

  if (typeof page === 'number') {
    if (page > 1) {
      params.set('page', String(page));
    } else {
      params.delete('page');
    }
  }

  logger.debug({ params }, 'Built MsCatalog2 request params');

  return params;
};

const clamp = (val: number, min: number, max: number) =>
  Math.min(Math.max(val, min), max);

export const initializeFiltersFromSearchParams = (
  searchParams: SearchParamsT,
  availableFilters: {
    categories?: { id: string; name: string }[];
    brands?: { id: string; name: string }[];
    priceRange?: { min: number; max: number };
  },
): {
  filters: SelectedFilters;
  sort: { sortBy: OfferSortBy; sortDirection: OfferSortByDirection };
} => {
  const {
    categories: catFilters,
    brands: brandFilters,
    priceRange,
  } = availableFilters;

  // --- Categories
  const categories =
    searchParams?.categoryIds
      ?.map((id) => catFilters?.find((c) => c.id === id))
      ?.filter((c): c is { id: string; name: string } => Boolean(c)) ?? [];

  // --- Brands
  const brands =
    searchParams?.brandIds
      ?.map((id) => brandFilters?.find((b) => b.id === id))
      ?.filter((b): b is { id: string; name: string } => Boolean(b)) ?? [];

  // --- Price Range Min & Max
  const qMin =
    typeof searchParams.minPrice === 'number'
      ? searchParams.minPrice
      : Number(searchParams.minPrice);

  const qMax =
    typeof searchParams.maxPrice === 'number'
      ? searchParams.maxPrice
      : Number(searchParams.maxPrice);

  let safePriceRange: { min: number; max: number } | undefined;

  if (!Number.isNaN(qMin) && !Number.isNaN(qMax) && priceRange) {
    const rawMin = Math.min(qMin, qMax);
    const rawMax = Math.max(qMin, qMax);
    const safeMin = clamp(rawMin, priceRange.min, priceRange.max);
    const safeMax = clamp(rawMax, safeMin, priceRange.max);

    safePriceRange = { min: safeMin, max: safeMax };
  }

  // --- Variants (unchanged)
  const variants: Record<string, string[]> =
    typeof searchParams.variants === 'object' && searchParams.variants !== null
      ? Object.fromEntries(
          Object.entries(searchParams.variants).map(([variantId, values]) => [
            variantId,
            Array.isArray(values)
              ? values.filter((v): v is string => typeof v === 'string')
              : [],
          ]),
        )
      : {};

  // --- Sort By
  const sortBy = (searchParams.sortBy as OfferSortBy) ?? DEFAULT_SORT_BY;
  // --- Sort Direction
  const sortDirection =
    (searchParams.sortDirection as OfferSortByDirection) ??
    DEFAULT_SORT_BY_DIRECTION;

  // --- Final filters
  const filters: SelectedFilters = {
    categories,
    brands,
    variants,
    priceRange:
      safePriceRange ??
      (priceRange ? { min: priceRange.min, max: priceRange.max } : undefined),
  };

  return { filters, sort: { sortBy, sortDirection } };
};

export const toggleFilterLogic = (
  prev: SelectedFilters,
  type: FilterType,
  id: string,
  name: string,
): SelectedFilters => {
  if (type === FilterType.VARIANT) {
    return prev;
  }
  const current = prev[type] as { id: string; name: string }[];
  const exists = current.find((c) => c.id === id);
  const next = exists
    ? current.filter((c) => c.id !== id)
    : [...current, { id, name }];

  return { ...prev, [type]: next };
};

export const toggleVariantLogic = (
  prev: SelectedFilters,
  variantId: string,
  value: string,
): SelectedFilters => {
  const current = new Set(prev.variants[variantId] || []);

  if (current.has(value)) {
    current.delete(value);
  } else {
    current.add(value);
  }
  const variants = { ...prev.variants };

  if (current.size > 0) {
    variants[variantId] = Array.from(current);
  } else {
    delete variants[variantId];
  }

  return { ...prev, variants };
};

export const updatePriceRange = (
  prev: SelectedFilters,
  min: number,
  max: number,
  defaultRange: { min: number; max: number },
): SelectedFilters => {
  const orderedMin = Math.min(min, max);
  const orderedMax = Math.max(min, max);
  const safeMin = Math.min(
    Math.max(orderedMin, defaultRange.min),
    defaultRange.max,
  );
  const safeMax = Math.min(Math.max(orderedMax, safeMin), defaultRange.max);

  return { ...prev, priceRange: { min: safeMin, max: safeMax } };
};

export const parseSearchParams = (
  urlParams: Record<string, any> | null,
): SearchParamsT => {
  const params: SearchParamsT = {};

  if (!urlParams) {
    return params;
  }

  const parseArray = (val: any): string[] => {
    if (!val) {
      return [];
    }

    if (Array.isArray(val)) {
      return val.map((v) => v.trim()).filter(Boolean);
    }

    // Convert comma-separated strings into arrays
    if (typeof val === 'string') {
      return val
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
    }

    return [];
  };

  const parseNumber = (val: any): number | undefined => {
    const num = Number(val);

    return Number.isNaN(num) ? undefined : num;
  };

  // Sort By
  if (urlParams.sortBy) {
    params.sortBy = urlParams.sortBy;
  }

  // Sort Direction
  if (urlParams.sortDirection) {
    params.sortDirection = urlParams.sortDirection;
  }

  // Category Ids
  if (urlParams.categoryIds) {
    params.categoryIds = parseArray(urlParams.categoryIds);
  }

  // Brand Ids
  if (urlParams.brandIds) {
    params.brandIds = parseArray(urlParams.brandIds);
  }

  // Min Price
  if (urlParams.minPrice) {
    params.minPrice = parseNumber(urlParams.minPrice);
  }

  // Max Price
  if (urlParams.maxPrice) {
    params.maxPrice = parseNumber(urlParams.maxPrice);
  }
  // Variants (flattened keys like "variants[var-id][]")
  const variants: Record<string, string[]> = {};

  Object.entries(urlParams).forEach(([key, value]) => {
    const match = key.match(/^variants\[(.+)\]\[\]$/);

    if (match) {
      const variantId = match[1];

      variants[variantId] = parseArray(value);
    }
  });

  if (Object.keys(variants).length > 0) {
    params.variants = variants;
  }

  return params;
};

// Get Server Side Props
export const getPageFromUrl = (urlParams: ParsedUrlQuery | null): number => {
  const raw = Array.isArray(urlParams?.page)
    ? urlParams?.page[0]
    : urlParams?.page;
  const page = Math.floor(Number(raw));

  return Number.isFinite(page) && page > 1 ? page : 1;
};

export const DEPRECATED_HANDLES_MAP: Record<string, string> = {
  // iPhone Series
  'apple-iphone-7-series': 'apple-iphone-7',
  'apple-iphone-8-series': 'apple-iphone-8',
  'apple-iphone-x-series': 'apple-iphone-x',
  'apple-iphone-se-series': 'apple-iphone-se-3rd-gen',
  'apple-iphone-11-series': 'apple-iphone-11',
  'apple-iphone-12-series': 'apple-iphone-12',
  'apple-iphone-13-series': 'apple-iphone-13',
  'apple-iphone-14-series': 'apple-iphone-14',
  'apple-iphone-15-series': 'apple-iphone-15',
  // Samsung Galaxy Series
  'samsung-galaxy-a-series': 'samsung-galaxy-a57',
  'samsung-galaxy-m-series': 'galaxy-m54',
  'samsung-galaxy-note-series': 'samsung-galaxy-note-20',
  'samsung-galaxy-s9-series': 'samsung-galaxy-s9',
  'samsung-galaxy-s10-series': 'samsung-galaxy-s10',
  'samsung-galaxy-z-series': 'samsung-galaxy-z-fold7',
  'samsung-galaxy-s20-series': 'samsung-galaxy-s20',
  'samsung-galaxy-s21-series': 'samsung-galaxy-s21',
  'galaxy-s22-series': 'galaxy-s22-5g',
  'samsung-galaxy-s23-series': 'samsung-galaxy-s23',
  'samsung-galaxy-s24-series': 'samsung-galaxy-s24',
  // iPads
  'apple-ipad-air': 'apple-ipads',
  'apple-ipad-mini': 'apple-ipads',
  'apple-ipad-pro': 'apple-ipads',
  ipads: 'apple-ipads',
  'apple-ipad': 'apple-ipads',
  // MacBooks
  'apple-macbook-12-inch': 'apple-macbooks',
  'apple-macbook-air': 'apple-macbooks',
  'apple-macbook-pro': 'apple-macbooks',
  macbooks: 'apple-macbooks',
};
